import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CButton, CButtonGroup } from '@coreui/react-pro'

import EntityPage from '../components/EntityPage'
import StatusBadge from '../components/StatusBadge'
import { fmtMoney } from '../components/format'
import { usePersonLabels } from '../api/people'
import { api } from '../api/client'

const CURRENCIES = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
  { value: 'CRC', label: 'CRC — Costa Rican Colón' },
]

const DISPLAY_OPTIONS = ['Original', 'USD', 'CAD', 'CRC']

// On this screen USD gets a US$ label — CA$ and US$ amounts sit side by
// side, so every dollar figure must say which dollar it is.
const fmt = (n: number | null | undefined, currency: string) =>
  fmtMoney(n, currency, { USD: 'US$' })

interface FxRates {
  base: string
  rates: Record<string, number>
  source: string
  fetched_at: string | null
}

const Money: React.FC = () => {
  const labels = usePersonLabels()
  const [display, setDisplay] = useState('Original')

  const fx = useQuery({
    queryKey: ['fx-rates'],
    queryFn: () => api.get<FxRates>('/v1/fx/rates'),
    staleTime: 60 * 60 * 1000,
  })
  const rates = fx.data?.rates
  const converting = display !== 'Original'

  // amount in `from` currency -> the currently displayed currency
  const convert = (amount: number, from: string): number => {
    if (!converting || from === display) return amount
    const fromRate = rates?.[from]
    const toRate = rates?.[display]
    if (!fromRate || !toRate) return amount
    return (amount / fromRate) * toRate
  }
  const shownCurrency = (row: { currency: string }) =>
    converting ? display : row.currency

  const columns = useMemo(() => {
    const suffix = converting ? ` (${display})` : ''
    return [
      { key: 'item', label: 'Item' },
      { key: 'paid_by_id', label: 'Paid by', render: (r: any) => labels[r.paid_by_id] ?? '' },
      { key: 'amount', label: `Amount${suffix}`, render: (r: any) => fmt(convert(r.amount, r.currency), shownCurrency(r)) },
      { key: 'currency', label: 'Currency' },
      { key: 'split_among', label: 'Split among' },
      { key: 'per_person', label: `Per person${suffix}`, render: (r: any) => fmt(convert(r.per_person, r.currency), shownCurrency(r)) },
      { key: 'status', label: 'Status', render: (r: any) => <StatusBadge status={r.status} /> },
      { key: 'notes', label: 'Notes' },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels, display, rates])

  const footer = (rows: any[]) => {
    if (rows.length === 0) return null
    if (!converting) {
      // Original mode: mixed currencies can't be summed — one total per
      // currency, symbols (CA$ / US$ / ₡) doing the labeling
      const byCurrency = new Map<string, { total: number; perPerson: number }>()
      for (const r of rows) {
        const agg = byCurrency.get(r.currency) ?? { total: 0, perPerson: 0 }
        agg.total += r.amount
        agg.perPerson += r.per_person
        byCurrency.set(r.currency, agg)
      }
      return (
        <div className="mt-3 d-flex flex-wrap justify-content-end gap-4 fs-5">
          {[...byCurrency.entries()].map(([cur, agg]) => (
            <span key={cur} className="text-body-secondary">
              <span className="fw-bold text-body">{fmt(agg.total, cur)}</span>{' '}
              · {fmt(agg.perPerson, cur)}/person
            </span>
          ))}
        </div>
      )
    }
    const total = rows.reduce((sum, r) => sum + convert(r.amount, r.currency), 0)
    const perPerson = rows.reduce((sum, r) => sum + convert(r.per_person, r.currency), 0)
    const approx = fx.data?.source === 'fallback' ? ' (approx)' : ''
    return (
      <div className="mt-3 d-flex flex-wrap justify-content-end align-items-baseline gap-3">
        <span className="fs-4 fw-bold">
          Total {fmt(total, display)}{approx}
        </span>
        <span className="fs-5 text-body-secondary">
          {fmt(perPerson, display)} per person
        </span>
        {display !== 'USD' && rates?.[display] != null && (
          <span className="small text-body-secondary">
            1 USD ≈ {fmtMoney(rates[display], display)}
          </span>
        )}
      </div>
    )
  }

  return (
    <EntityPage
      entity="expenses"
      title="💰 Money"
      description="Shared costs. Each amount stays in its own currency; pick a display currency to convert everything and see the totals."
      addLabel="Add expense"
      emptyHint="No shared costs logged yet."
      columns={columns}
      toolbar={
        <CButtonGroup size="sm" role="group" aria-label="Display currency">
          {DISPLAY_OPTIONS.map((option) => (
            <CButton
              key={option}
              color="secondary"
              variant="outline"
              active={option === display}
              disabled={option !== 'Original' && !rates}
              onClick={() => setDisplay(option)}
            >
              {option}
            </CButton>
          ))}
        </CButtonGroup>
      }
      footer={footer}
      fields={[
        { name: 'item', label: 'Item', type: 'text', required: true, placeholder: 'House deposit' },
        { name: 'paid_by_id', label: 'Paid by', type: 'person' },
        { name: 'amount', label: 'Amount', type: 'number', required: true },
        { name: 'currency', label: 'Currency', type: 'select', options: CURRENCIES },
        { name: 'split_among', label: 'Split among (# people)', type: 'number', required: true },
        {
          name: 'status', label: 'Status', type: 'select',
          options: [
            { value: 'paid', label: 'Paid' },
            { value: 'owed', label: 'Owed' },
            { value: 'settled', label: 'Settled' },
          ],
        },
        { name: 'notes', label: 'Notes', type: 'textarea' },
      ]}
      blank={{ split_among: 11, status: 'paid', currency: 'USD' }}
    />
  )
}

export default Money
